import { ReactifySearchProviderProps } from "../components";
import { Config, ConfigCuration, ConfigSort } from "../types";

export const getSortOptions = (options: {
  provider: ReactifySearchProviderProps;
  config: Config;
  sortOption?: string;
}) => {
  return options.config.sort
    .sort((a, b) => {
      const aInt =
        typeof a.position === "string"
          ? parseInt(a.position, 10)
          : a.position;
      const bInt =
        typeof b.position === "string"
          ? parseInt(b.position, 10)
          : b.position;
      return aInt - bInt;
    })
    .filter(({ visibility }) => ["all", options.provider.mode].includes(visibility));

}

export const getSortOption = (options: {
  provider: ReactifySearchProviderProps;
  config: Config;
  sortOption?: string;
  query?: Record<string, string>;
}) => {
  const sortOptions = getSortOptions(options);

  return sortOptions.find(({ handle }) => handle === options.sortOption || handle === options.query?.sort) ?? sortOptions[0];
}

export const generateSortSort = (options: {
  provider: ReactifySearchProviderProps;
  config: Config;
  curation?: ConfigCuration;
  sortOption?: ConfigSort;
}) => {
  // curation positions are only applied for the default `collections.position` or `_score` sort
  // if the sort is something else, apply a normal sort which applies what the user has requested
  if (options.provider.mode !== "instant-search") {
    if (
      options.sortOption &&
      !["_score", "collections.position"].includes(options.sortOption.field)
    ) {
      return [{ [options.sortOption.field]: options.sortOption.direction }];
    }
  }

  const sorts = [];

  // collection curation pin sorting
  if (options.provider.mode === "collection" && options.provider.collectionHandle) {
    if (options.curation?.collectionHandle) {
      const queries: Array<unknown> = [{
        term: {
          "curations.collectionHandle.keyword": options.curation.collectionHandle,
        },
      }];
      if (options.provider.market) {
        queries.push({
          term: {
            "curations.markets.keyword": options.provider.market,
          },
        });
      }
      sorts.push({
        "curations.position": {
          unmapped_type: "long",
          order: "asc",
          nested: {
            path: "curations",
            filter: {
              bool: {
                must: queries
              }
            }
          },
        },
      });
    }
  }

  // search options.curation pin sorting
  if (options.provider.mode === "search" || options.provider.mode === "instant-search") {
    if (options.curation?.searchTerm) {
      const queries: Array<unknown> = [{
        term: {
          "curations.searchTerm.keyword": options.curation.searchTerm.toLowerCase(),
        },
      }];
      if (options.provider.market) {
        queries.push({
          term: {
            "curations.markets.keyword": options.provider.market,
          },
        });
      }
      sorts.push({
        "curations.position": {
          unmapped_type: "long",
          order: "asc",
          nested: {
            path: "curations",
            filter: {
              bool: {
                must: queries
              }
            }
          },
        },
      });
    }
  }

  // options.curation boost sorting
  if (options.curation) {
    if (0 < options.curation.boosting.groupings.length) {
      const groupings = options.curation.boosting.groupings.sort((a, b) =>
        a.position > b.position ? 1 : -1
      );

      for (const grouping of groupings) {
        try {
          const query = JSON.parse(grouping.query);
          if (query) sorts.push(query);
        } catch {
          console.error(
            `query could not be parsed for boost grouping`,
            grouping
          );
        }
      }
    }

    if (0 < options.curation.boosting.sortings.length) {
      const sortings = options.curation.boosting.sortings.sort((a, b) =>
        a.position > b.position ? 1 : -1
      );

      for (const sorting of sortings) {
        try {
          const query = JSON.parse(sorting.query);
          if (query) sorts.push(query);
        } catch {
          console.error(`query could not be parsed for boost sorting`, sorting);
        }
      }
    }
  }

  // default collection sorting
  if (options.provider.mode === "collection" && options.provider.collectionHandle) {
    sorts.push({
      "collections.position": {
        order: "asc",
        nested: {
          path: "collections",
          filter: {
            term: {
              "collections.handle.keyword": options.provider.collectionHandle,
            },
          },
        },
      },
    });
  }

  // default search sorting
  if (options.provider.mode === "search" || options.provider.mode === "instant-search") {
    sorts.push("_score");
  }

  return sorts;
}

export const generateSortQuery = (options: {
  curation?: ConfigCuration;
  sortOption?: ConfigSort;
}): Record<string, any> | undefined => {
  if (!options.curation || options.curation.id.includes("global")) {
    return { match_all: {} };
  }

  return {
    bool: {
      must_not: [
        {
          nested: {
            path: "curations",
            query: {
              bool: {
                must: [
                  {
                    term: {
                      [`curations.${
                        "collection" === options.curation.type
                          ? "collectionHandle"
                          : "searchTerm"
                      }.keyword`]:
                        "collection" === options.curation.type
                          ? options.curation.collectionHandle
                          : options.curation.searchTerm?.toLowerCase(),
                    },
                  },
                  {
                    term: {
                      "curations.hidden": true,
                    },
                  },
                ],
              },
            },
          },
        },
        // hide callout when not sorting by _score or collections.position
        ...(options.sortOption &&
        !["_score", "collections.position"].includes(options.sortOption.field)
          ? [
              {
                term: {
                  type: "callout",
                },
              },
            ]
          : []),
      ],
    },
  };
};
